import { useContext, useMemo, type JSX } from "react";

import { Trans } from "@lingui/react";
import { MSAxiosInstance } from "@mobsuccess-devops/rdc-client/axios-instance";
import { Loader } from "@mobsuccess-devops/react-ui/Loader";
import { useQuery } from "@tanstack/react-query";

import type { AxiosInstances } from "../../features/axios-instances";
import { AuthContext } from "../../public/auth";

import { AxiosContext, type AxiosContextType } from "./context";

MSAxiosInstance.defaults.baseURL =
  import.meta.env.VITE_DATA_CONTEXT_SERVICE_RDC;

type AxiosContextProviderProps = {
  orchestrator: AxiosInstances;
  children: JSX.Element | null;
};

export function AxiosContextProvider({
  children,
  orchestrator,
}: AxiosContextProviderProps): JSX.Element | null {
  const authContext = useContext(AuthContext);
  const bearer = authContext?.bearer ?? null;

  const value = useMemo<AxiosContextType>(
    () => ({ orchestrator }),
    [orchestrator],
  );

  const { isLoading } = useQuery({
    enabled: !!bearer,
    queryKey: ["load.url.overrides"],
    queryFn: orchestrator.loadUrlOverrides(bearer),
    staleTime: Number.POSITIVE_INFINITY, // bearer is not cause to invalidation
  });

  if (isLoading) {
    return (
      <Loader.Block>
        <Loader.Ellipsis />
        <Loader.Description>
          <Trans id="axios-context-provider.setting-up-environnement" />
        </Loader.Description>
      </Loader.Block>
    );
  }

  return <AxiosContext value={value}>{children}</AxiosContext>;
}
